<template>
  <div ref="rootEl" class="relative">
    <IconButton size="md" :label="t('shell.accountMenu')" aria-haspopup="menu" :aria-expanded="open" @click="open = !open">
      <MenuBars size="18px" aria-hidden="true" />
    </IconButton>

    <Transition name="user-menu">
      <div v-if="open" role="menu" class="border-hairline bg-surface absolute top-full right-0 z-40 mt-2 w-66 overflow-hidden rounded-2xl border shadow-lg shadow-black/5 dark:shadow-black/40">
        <div class="border-hairline border-b px-4.5 py-3">
          <div class="font-body text-faint text-[11px]">{{ t('shell.signedInAs') }}</div>
          <div class="font-display text-ink mt-0.5 truncate text-[13px] font-semibold">{{ userName }}</div>
        </div>
        <div class="flex flex-col p-1.5">
          <MenuItem @click="openSupport">
            <template #icon><HelpIcon size="17px" color="currentColor" /></template>
            {{ t('shell.support') }}
          </MenuItem>
          <MenuItem href="https://www.invoxia.com" target="_blank" rel="noopener" @click="open = false">
            <template #icon><ShoppingBag size="17px" /></template>
            {{ t('shell.store') }}
          </MenuItem>
          <MenuItem tone="danger" @click="processLogout">
            <template #icon><Logout size="17px" /></template>
            {{ t('shell.logout') }}
          </MenuItem>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { getZendeskSsoUrl } from '@/api/support.api'
import HelpIcon from '@/components/svg/HelpIcon.vue'
import Logout from '@/components/svg/Logout.vue'
import MenuBars from '@/components/svg/MenuBars.vue'
import ShoppingBag from '@/components/svg/ShoppingBag.vue'
import IconButton from '@/components/ui/IconButton.vue'
import MenuItem from '@/components/ui/MenuItem.vue'
import { homeMessages } from '@/lang/pages/home'

const { t } = useI18n({ useScope: 'local', messages: homeMessages, inheritLocale: true })
const store = useStore()
const router = useRouter()
const route = useRoute()

const open = ref(false)
const rootEl = ref<HTMLElement | null>(null)

const userName = computed<string>(() => store.getters['users/getUserName'] || '')

const onDocumentClick = (e: MouseEvent) => {
  if (open.value && rootEl.value && !rootEl.value.contains(e.target as Node)) open.value = false
}
const onKeydown = (e: KeyboardEvent) => {
  if (open.value && e.key === 'Escape') open.value = false
}
onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
watch(
  () => route.path,
  () => (open.value = false),
)

const processLogout = () => {
  open.value = false
  store.dispatch('users/logout')
  store.dispatch('devices/removeDevices')
  router.push({ name: 'login' })
}

// "My requests" on the host-mapped Help Center; no locale segment so Zendesk
// serves the user's own language
const SUPPORT_REQUESTS_URL = 'https://support.invoxia.com/hc/requests'

const openSupport = async () => {
  open.value = false
  // Open the tab synchronously so popup blockers allow it, then point it to the SSO URL
  const win = window.open('', '_blank')
  try {
    const { data } = await getZendeskSsoUrl(SUPPORT_REQUESTS_URL)
    if (win) {
      win.opener = null
      win.location = data.url
    } else {
      window.location.href = data.url
    }
  } catch {
    if (win) win.close()
  }
}
</script>

<style scoped>
.user-menu-enter-active,
.user-menu-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.user-menu-enter-from,
.user-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
</style>
